import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Plus, Edit2, Trash2, ChevronRight, X } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, Modal, ModalHeader, ModalBody, ModalFooter, Input, Textarea, Select, EmptyState, DeleteConfirmModal } from '../../../components/ui'

interface Stage { id: number; name: string; color: string; is_initial: boolean; is_final: boolean; completes_demand: boolean; max_days: number | null; display_order: number }
interface Category { id: number; name: string; pipeline_id: number; parent_id: number | null; fields: any[]; is_active: boolean; children: Category[]; demands_count: number }
interface Pipeline { id: number; name: string; description: string | null; color: string | null; stages: Stage[] }

export default function DemandPipelineDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [pipeline, setPipeline] = useState<Pipeline | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Stage
  const [stageModalOpen, setStageModalOpen] = useState(false)
  const [editingStageId, setEditingStageId] = useState<number | null>(null)
  const [stageForm, setStageForm] = useState({ name: '', color: '#6b7280', is_initial: false, is_final: false, completes_demand: false, max_days: '' })
  const [stageErrors, setStageErrors] = useState<Record<string, string>>({})

  // Category
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null)
  const [categoryParentId, setCategoryParentId] = useState<number | null>(null)
  const [categoryForm, setCategoryForm] = useState({ name: '', fields: [] as any[] })
  const [categoryErrors, setCategoryErrors] = useState<Record<string, string>>({})

  // Field
  const [fieldModalOpen, setFieldModalOpen] = useState(false)
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null)
  const [fieldForm, setFieldForm] = useState({ key: '', label: '', type: 'text', required: false, options: [] as string[], stage: 'entry' })
  const [optionInput, setOptionInput] = useState('')

  const [deleteTarget, setDeleteTarget] = useState<{ type: string; id: number; name: string } | null>(null)

  useEffect(() => { fetchData() }, [id])

  async function fetchData() {
    setLoading(true)
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(buildApiUrl('/v1/demands/pipelines')),
        axios.get(buildApiUrl('/v1/demands/categories')),
      ])
      const found = pRes.data.data.find((p: Pipeline) => p.id === Number(id))
      setPipeline(found || null)
      setCategories(cRes.data.data.filter((c: Category) => c.pipeline_id === Number(id)))
    } catch { /* */ } finally { setLoading(false) }
  }

  // Stage CRUD
  function openStageCreate() {
    setEditingStageId(null)
    setStageForm({ name: '', color: '#6b7280', is_initial: false, is_final: false, completes_demand: false, max_days: '' })
    setStageErrors({}); setStageModalOpen(true)
  }

  function openStageEdit(s: Stage) {
    setEditingStageId(s.id)
    setStageForm({ name: s.name, color: s.color, is_initial: s.is_initial, is_final: s.is_final, completes_demand: s.completes_demand, max_days: s.max_days?.toString() || '' })
    setStageErrors({}); setStageModalOpen(true)
  }

  async function saveStage() {
    if (saving) return
    const e: Record<string, string> = {}
    if (!stageForm.name.trim()) e.name = 'Nome é obrigatório'
    setStageErrors(e); if (Object.keys(e).length > 0) return
    setSaving(true)
    try {
      const payload = { ...stageForm, max_days: stageForm.max_days ? Number(stageForm.max_days) : null }
      if (editingStageId) {
        await axios.put(buildApiUrl(`/v1/demands/stages/${editingStageId}`), payload)
        toast('success', 'Etapa atualizada')
      } else {
        await axios.post(buildApiUrl(`/v1/demands/pipelines/${id}/stages`), payload)
        toast('success', 'Etapa criada')
      }
      setStageModalOpen(false); fetchData()
    } catch (err: any) { toast('error', err.response?.data?.error || 'Erro ao salvar') } finally { setSaving(false) }
  }

  // Category CRUD
  function openCategoryCreate(parentId: number | null = null) {
    setEditingCategoryId(null); setCategoryParentId(parentId)
    setCategoryForm({ name: '', fields: [] }); setCategoryErrors({}); setCategoryModalOpen(true)
  }

  function openCategoryEdit(cat: Category) {
    setEditingCategoryId(cat.id); setCategoryParentId(cat.parent_id)
    setCategoryForm({ name: cat.name, fields: cat.fields || [] }); setCategoryErrors({}); setCategoryModalOpen(true)
  }

  async function saveCategory() {
    if (saving) return
    const e: Record<string, string> = {}
    if (!categoryForm.name.trim()) e.name = 'Nome é obrigatório'
    setCategoryErrors(e); if (Object.keys(e).length > 0) return
    setSaving(true)
    try {
      if (editingCategoryId) {
        await axios.put(buildApiUrl(`/v1/demands/categories/${editingCategoryId}`), categoryForm)
        toast('success', 'Categoria atualizada')
      } else {
        await axios.post(buildApiUrl('/v1/demands/categories'), { ...categoryForm, pipeline_id: Number(id), parent_id: categoryParentId })
        toast('success', 'Categoria criada')
      }
      setCategoryModalOpen(false); fetchData()
    } catch (err: any) { toast('error', err.response?.data?.error || 'Erro ao salvar') } finally { setSaving(false) }
  }

  // Field editor
  function openFieldCreate(stage: string = 'entry') {
    setEditingFieldIndex(null); setFieldForm({ key: '', label: '', type: 'text', required: false, options: [], stage }); setOptionInput(''); setFieldModalOpen(true)
  }
  function openFieldEdit(i: number) {
    const f = categoryForm.fields[i]
    setEditingFieldIndex(i); setFieldForm({ key: f.key, label: f.label, type: f.type, required: f.required || false, options: f.options || [], stage: f.stage || 'entry' }); setOptionInput(''); setFieldModalOpen(true)
  }
  function saveField() {
    const field: any = { key: fieldForm.key || fieldForm.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_'), label: fieldForm.label, type: fieldForm.type, required: fieldForm.required, stage: fieldForm.stage }
    if (fieldForm.type === 'select') field.options = fieldForm.options
    const newFields = [...categoryForm.fields]
    if (editingFieldIndex !== null) newFields[editingFieldIndex] = field; else newFields.push(field)
    setCategoryForm(f => ({ ...f, fields: newFields })); setFieldModalOpen(false)
  }
  function removeField(i: number) { setCategoryForm(f => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) })) }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      if (deleteTarget.type === 'stage') await axios.delete(buildApiUrl(`/v1/demands/stages/${deleteTarget.id}`))
      setDeleteTarget(null); toast('success', 'Removido'); fetchData()
    } catch (err: any) { toast('error', err.response?.data?.error || 'Erro ao remover') }
  }

  function renderCategory(cat: Category, depth = 0): React.ReactNode {
    return (
      <div key={cat.id}>
        <div className={`flex items-center justify-between py-2.5 px-3 hover:bg-gray-50 rounded-lg ${depth > 0 ? 'ml-6' : ''}`}>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-800 font-medium">{cat.name}</span>
            {cat.fields?.length > 0 && <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{cat.fields.length} campos</span>}
            {(cat.demands_count ?? 0) > 0 && <span className="text-[10px] text-gray-400">{cat.demands_count} demandas</span>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => openCategoryCreate(cat.id)} className="h-7 w-7 flex items-center justify-center rounded text-gray-400 hover:text-brand-500 hover:bg-brand-50" title="Subcategoria">
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => openCategoryEdit(cat)} className="h-7 w-7 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {cat.children?.map(child => renderCategory(child, depth + 1))}
      </div>
    )
  }

  if (loading) return <div className="space-y-4 animate-pulse"><div className="h-8 w-48 bg-gray-200 rounded" /><div className="h-32 bg-gray-100 rounded-xl" /><div className="h-32 bg-gray-100 rounded-xl" /></div>
  if (!pipeline) return <div className="text-center py-20 text-gray-500">Funil não encontrado</div>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/settings/demands')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          {pipeline.color && <span className="w-4 h-4 rounded-full" style={{ backgroundColor: pipeline.color }} />}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{pipeline.name}</h1>
            {pipeline.description && <p className="text-gray-500 text-sm">{pipeline.description}</p>}
          </div>
        </div>
      </div>

      {/* Etapas */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Etapas do funil</h3>
          <Button size="sm" variant="outline" icon={<Plus className="w-3.5 h-3.5" />} onClick={openStageCreate}>Nova etapa</Button>
        </div>

        {pipeline.stages.length === 0 ? (
          <EmptyState icon={<ChevronRight className="w-6 h-6" />} title="Nenhuma etapa" description="Adicione etapas ao funil." />
        ) : (
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {pipeline.stages.map((s, i) => (
              <div key={s.id} className="flex items-center gap-3 flex-shrink-0">
                <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 min-w-[160px] hover:shadow-sm transition-shadow cursor-pointer" onClick={() => openStageEdit(s)}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="text-sm font-medium text-gray-800">{s.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {!!s.is_initial && <Badge variant="brand" size="sm">Inicial</Badge>}
                    {!!s.is_final && !s.completes_demand && <Badge variant="gray" size="sm">Final</Badge>}
                    {!!s.completes_demand && <Badge variant="success" size="sm">Conclui</Badge>}
                    {s.max_days != null && s.max_days > 0 && <span className="text-[10px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">{s.max_days} dias</span>}
                  </div>
                </div>
                {i < pipeline.stages.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Categorias */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Categorias de demanda</h3>
          <Button size="sm" variant="outline" icon={<Plus className="w-3.5 h-3.5" />} onClick={() => openCategoryCreate()}>Nova categoria</Button>
        </div>

        {categories.length === 0 ? (
          <EmptyState icon={<Plus className="w-6 h-6" />} title="Nenhuma categoria" description="Adicione categorias para classificar as demandas." />
        ) : (
          <div>{categories.map(cat => renderCategory(cat))}</div>
        )}
      </Card>

      {/* Modal stage */}
      <Modal open={stageModalOpen} onClose={() => setStageModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setStageModalOpen(false)}>{editingStageId ? 'Editar Etapa' : 'Nova Etapa'}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <Input label="Nome" value={stageForm.name} onChange={e => { setStageForm(f => ({ ...f, name: e.target.value })); setStageErrors({}) }} placeholder="Ex: Recebida, Triagem" error={stageErrors.name} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Cor</label>
                <input type="color" value={stageForm.color} onChange={e => setStageForm(f => ({ ...f, color: e.target.value }))} className="h-10 w-full rounded-lg border border-gray-300 cursor-pointer" />
              </div>
              <Input label="Prazo (dias)" type="number" value={stageForm.max_days} onChange={e => setStageForm(f => ({ ...f, max_days: e.target.value }))} placeholder="SLA" />
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={stageForm.is_initial} onChange={e => setStageForm(f => ({ ...f, is_initial: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                <div><span className="text-sm text-gray-700">Etapa inicial</span><p className="text-xs text-gray-400">Onde a demanda nasce</p></div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={stageForm.is_final} onChange={e => setStageForm(f => ({ ...f, is_final: e.target.checked, completes_demand: e.target.checked ? stageForm.completes_demand : false }))} className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                <div><span className="text-sm text-gray-700">Etapa final</span><p className="text-xs text-gray-400">Encerra a demanda</p></div>
              </label>
              {stageForm.is_final && (
                <label className="flex items-center gap-3 cursor-pointer ml-7">
                  <input type="checkbox" checked={stageForm.completes_demand} onChange={e => setStageForm(f => ({ ...f, completes_demand: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-success-500 focus:ring-success-500" />
                  <div><span className="text-sm text-gray-700">Conclui com sucesso</span><p className="text-xs text-gray-400">Muda status para "Concluída"</p></div>
                </label>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          {editingStageId && <Button variant="outline" className="text-error-500" onClick={() => { setStageModalOpen(false); setDeleteTarget({ type: 'stage', id: editingStageId, name: stageForm.name }) }}>Remover</Button>}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => setStageModalOpen(false)}>Cancelar</Button>
          <Button onClick={saveStage} loading={saving}>{editingStageId ? 'Salvar' : 'Criar'}</Button>
        </ModalFooter>
      </Modal>

      {/* Modal category */}
      <Modal open={categoryModalOpen} onClose={() => setCategoryModalOpen(false)} size="md">
        <ModalHeader onClose={() => setCategoryModalOpen(false)}>{editingCategoryId ? 'Editar Categoria' : 'Nova Categoria'}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <Input label="Nome" value={categoryForm.name} onChange={e => { setCategoryForm(f => ({ ...f, name: e.target.value })); setCategoryErrors({}) }} placeholder="Ex: Cirurgia, Exame de sangue" error={categoryErrors.name} />
            {/* Campos de entrada */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Campos de entrada</label>
                <button onClick={() => openFieldCreate('entry')} className="text-xs text-brand-500 hover:text-brand-600 font-medium flex items-center gap-1"><Plus className="w-3 h-3" /> Adicionar</button>
              </div>
              <p className="text-[11px] text-gray-400 mb-2">Preenchidos ao abrir a demanda</p>
              {categoryForm.fields.filter((f: any) => (f.stage || 'entry') === 'entry').length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-3 border border-dashed border-gray-200 rounded-lg">Nenhum campo de entrada</p>
              ) : (
                <div className="space-y-2">
                  {categoryForm.fields.map((f: any, i: number) => (f.stage || 'entry') === 'entry' && (
                    <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                      <div><span className="text-sm text-gray-800">{f.label}</span><span className="text-xs text-gray-400 ml-2">{f.type}{f.required ? ' · obrigatório' : ''}</span></div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openFieldEdit(i)} className="h-6 w-6 flex items-center justify-center text-gray-400 hover:text-gray-600"><Edit2 className="w-3 h-3" /></button>
                        <button onClick={() => removeField(i)} className="h-6 w-6 flex items-center justify-center text-gray-400 hover:text-error-500"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Campos de encerramento */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Campos de encerramento</label>
                <button onClick={() => openFieldCreate('closing')} className="text-xs text-brand-500 hover:text-brand-600 font-medium flex items-center gap-1"><Plus className="w-3 h-3" /> Adicionar</button>
              </div>
              <p className="text-[11px] text-gray-400 mb-2">Preenchidos ao concluir a demanda</p>
              {categoryForm.fields.filter((f: any) => f.stage === 'closing').length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-3 border border-dashed border-gray-200 rounded-lg">Nenhum campo de encerramento</p>
              ) : (
                <div className="space-y-2">
                  {categoryForm.fields.map((f: any, i: number) => f.stage === 'closing' && (
                    <div key={i} className="flex items-center justify-between px-3 py-2 bg-amber-50 rounded-lg">
                      <div><span className="text-sm text-gray-800">{f.label}</span><span className="text-xs text-gray-400 ml-2">{f.type}{f.required ? ' · obrigatório' : ''}</span></div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openFieldEdit(i)} className="h-6 w-6 flex items-center justify-center text-gray-400 hover:text-gray-600"><Edit2 className="w-3 h-3" /></button>
                        <button onClick={() => removeField(i)} className="h-6 w-6 flex items-center justify-center text-gray-400 hover:text-error-500"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setCategoryModalOpen(false)}>Cancelar</Button>
          <Button onClick={saveCategory} loading={saving}>{editingCategoryId ? 'Salvar' : 'Criar'}</Button>
        </ModalFooter>
      </Modal>

      {/* Modal field */}
      <Modal open={fieldModalOpen} onClose={() => setFieldModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setFieldModalOpen(false)}>{editingFieldIndex !== null ? 'Editar Campo' : 'Novo Campo'}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <Input label="Nome do campo" value={fieldForm.label} onChange={e => setFieldForm(f => ({ ...f, label: e.target.value, key: e.target.value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_') }))} placeholder="Ex: Hospital, Data da cirurgia" />
            <Select label="Tipo" value={fieldForm.type} onChange={e => setFieldForm(f => ({ ...f, type: e.target.value }))}
              options={[{ value: 'text', label: 'Texto' }, { value: 'number', label: 'Número' }, { value: 'date', label: 'Data' }, { value: 'select', label: 'Seleção' }, { value: 'textarea', label: 'Texto longo' }, { value: 'boolean', label: 'Sim/Não' }]} />
            {fieldForm.type === 'select' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opções</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {fieldForm.options.map((opt, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-sm px-2.5 py-1 rounded-lg">
                      {opt}
                      <button type="button" onClick={() => setFieldForm(f => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }))} className="text-gray-400 hover:text-gray-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  value={optionInput}
                  onChange={e => setOptionInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      const val = optionInput.trim()
                      if (val && !fieldForm.options.includes(val)) {
                        setFieldForm(f => ({ ...f, options: [...f.options, val] }))
                        setOptionInput('')
                      }
                    }
                  }}
                  placeholder="Digite e pressione Enter"
                  className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all"
                />
              </div>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={fieldForm.required} onChange={e => setFieldForm(f => ({ ...f, required: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
              <span className="text-sm text-gray-700">Campo obrigatório</span>
            </label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setFieldModalOpen(false)}>Cancelar</Button>
          <Button onClick={saveField} disabled={!fieldForm.label}>{editingFieldIndex !== null ? 'Salvar' : 'Adicionar'}</Button>
        </ModalFooter>
      </Modal>

      <DeleteConfirmModal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete}
        title="Remover etapa" message={<>Tem certeza que deseja remover <strong>"{deleteTarget?.name}"</strong>?</>} confirmLabel="Remover" />
    </div>
  )
}
